import React, { useMemo, useContext } from 'react';
import { AuthContext } from 'contexts';
import { Link } from 'react-router-dom';
import { featureEnabled, paramaterize } from 'utils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import styled from 'styled-components';
import { useListings } from '../hooks';
import { ListingLayoutCard, Loading } from '../components';
import ListingsSection from '../Section/ListingsSection';

const ListingsCard: React.FC = () => {
  const { auth, orgPlan } = useContext(AuthContext);
  const linkedAccounts = auth?.linkedAccounts;
  const activeAccount = linkedAccounts?.length ? linkedAccounts[0] : null;
  const hasLinkAccountPhase2Feature = featureEnabled(
    'linkAccountPhaseFeature',
    2,
  );
  const {
    availabilityOptions,
    availability,
    filteredListingsByAvailability: listings,
    onStatusChange,
    onAvailabilityChange,
    isLoading,
    searchValue,
    setSearchValue,
  } = useListings({ searchType: 'user' });

  const redirectUrl = useMemo(() => {
    let url = `/listing/list/brokerage/0?agent=${auth?.id}`;
    if (orgPlan?.name === 'AGENT') {
      url = `/listing/list/user/0?${paramaterize({
        searchType: 'user',
        status: 'All',
        location: 'All',
        locationAddress: 'All',
        avail: 'All',
        type: 'user',
      })}`;
    }
    return url;
  }, [auth?.id, orgPlan?.name]);

  const actions = useMemo(
    () =>
      listings.length > 0
        ? {
            actions: [
              <Link key="view-all-link" to={redirectUrl}>
                View All
              </Link>,
            ],
          }
        : {},
    [listings, redirectUrl],
  );

  return (
    <ListingLayoutCard
      search={{
        searchValue,
        onSearchValueChange: setSearchValue,
      }}
      titleLink={{
        title: (
          <>
            <span data-cy="view-all-listings">Listings</span>{' '}
            {hasLinkAccountPhase2Feature && (
              <>
                <AccountBoardSpan>({activeAccount?.board})</AccountBoardSpan>{' '}
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <LeftAlignedTooltip id="tooltip-listings-info">
                      <strong>
                        {`Showing ${activeAccount?.board} listings`}
                      </strong>{' '}
                      <br />
                      {`Don’t see your listing? It might be under a different MLS. Try switching accounts from the account dropdown in the header.`}
                    </LeftAlignedTooltip>
                  }
                >
                  <i className="fa fa-info-circle m-l-sm font-size-16"></i>
                </OverlayTrigger>
              </>
            )}
          </>
        ),
        linkPath: `/listing/list/user/0?${paramaterize({
          searchType: 'user',
          status: 'All',
          location: 'All',
          locationAddress: 'All',
          avail: 'All',
          type: 'user',
        })}`,
      }}
      onStatusChange={onStatusChange}
      onAvailabilityChange={onAvailabilityChange}
      availabilityOptions={availabilityOptions}
      availability={availability}
      data-cy="dashboard-listings-card"
      {...actions}
    >
      {isLoading ? <Loading /> : <ListingsSection listings={listings} />}
    </ListingLayoutCard>
  );
};

const AccountBoardSpan = styled.span`
  font-weight: 400;
`;

const LeftAlignedTooltip = styled(Tooltip)`
  .tooltip-inner {
    text-align: left;
  }
`;

export default ListingsCard;
